import { X } from "lucide-react"

import { Avatar } from "@/components/tuw/Avatar"
import { messageFreelancer } from "@/lib/hireActions"

// The inbox's "New message" picker: the real freelancers the customer has
// worked with (CustomerMessagesPresenter#compose_people).
//
// Two kinds of pick, decided by the server. `openId` is a row the inbox list
// renders, so the pick selects it in place. Everyone else with a `slug` is
// reached through the same route as the profile's Message button, which opens
// (or creates) the chat and lands back in the inbox with that thread selected
// — gated, like that button, by the account's direct-messaging permission
// (`canMessage`; the server refuses too). A person with neither — an employee
// with no login — cannot be messaged and stays disabled. The picker used to
// offer a freelancer whose DM sat outside the rendered list, then select
// nothing, and disabled everyone with no DM at all (UPDA-97).
export function composePick(person, { select, canMessage = true }) {
  if (person.openId) {
    select(person.openId)
    return "select"
  }
  if (person.slug && canMessage) {
    messageFreelancer(person.slug)
    return "open"
  }
  return null
}

function hint(person, canMessage) {
  if (person.openId) return null
  if (!person.slug) return " · can't be messaged"
  return canMessage ? " · start a chat" : " · messaging is off for your account"
}

export default function ComposeModal({ people, onPick, onClose, canMessage = true }) {
  return (
    <div
      className="fixed inset-0 z-90 flex items-start justify-center bg-[rgba(20,20,18,0.4)] pt-[90px]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-label="New message"
        className="w-[440px] max-w-[92vw] overflow-hidden rounded-2xl border border-line-2 bg-card shadow-[0_30px_70px_-20px_rgba(0,0,0,0.4)]"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="font-display text-[15px] font-bold">New message</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid size-[30px] place-items-center rounded-lg text-ink-3 hover:bg-secondary hover:text-foreground"
          >
            <X className="size-[18px]" strokeWidth={2} />
          </button>
        </div>
        <div className="px-5 pt-3.5 pb-5">
          <div className="mb-2 text-[11px] font-bold tracking-[0.04em] text-ink-3 uppercase">
            Message a freelancer you've worked with
          </div>
          <div className="flex max-h-[300px] flex-col gap-1 overflow-y-auto">
            {people.length === 0 && (
              <p className="px-2.5 py-4 text-[12.5px] text-ink-3">
                Once you've worked with a freelancer they'll show up here so you can message them directly.
              </p>
            )}
            {people.map((p) => (
              <button
                key={p.slug ?? p.name}
                type="button"
                onClick={() => onPick(p)}
                disabled={!p.openId && !(p.slug && canMessage)}
                className="flex items-center gap-[11px] rounded-[10px] px-2.5 py-[9px] text-left enabled:hover:bg-accent disabled:opacity-60"
              >
                <Avatar initials={p.initials} name={p.name} size={36} />
                <div className="min-w-0">
                  <h4 className="font-display text-[13.5px] font-semibold">{p.name}</h4>
                  <div className="text-[11.5px] text-ink-3">
                    {p.role}
                    {hint(p, canMessage)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
